import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, deleteField, doc, getDocs, updateDoc } from 'firebase/firestore';
import { FiAlertCircle, FiCheckCircle, FiExternalLink, FiPause, FiPlay, FiRefreshCw } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { formatDate, toInputDate } from '../../lib/format';
import { normalizeLinks } from '../../lib/links';
import { useSessionUser } from '../../lib/permissions';
import { getAreaGrant } from '../../lib/session';
import { documentError, importLink, reportImport, storedDocuments } from '../../lib/documents';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { BUTTON, CARD, Notice, SIZE, SkeletonRows } from '../ui/kit';
import { useDocumentsStatus } from './AttachmentsField';
import { examDate, patientName } from '../echo/echoModel';
import { legacyLinks as legacyProgrammeLinks, periodLabel, periodOf, titleOf } from '../programme/programmeModel';
import { titleOf as scheduleTitleOf } from '../schedule/scheduleModel';

const asDocument = ({ id, name, mimeType }) => ({ id, name, mimeType: mimeType || '' });
const withAdded = (current, added) => [...current, ...added.filter((item) => !current.some((stored) => stored.id === item.id))].map(asDocument);

// Where each section keeps its former Dropbox links, and how an entry is updated once its
// files are in the service's Dropbox (the links that could not be copied stay in place).
const SOURCES = [
  {
    collection: 'fichiers',
    section: 'fichiers',
    label: 'Fichiers des dossiers',
    links: (entry) => normalizeLinks(entry.dropboxLinks),
    fields: (entry) => ({ patientId: entry.patientId, unit: entry.service, date: toInputDate(entry.datePatient) }),
    update: (entry, added, kept) => ({ documents: withAdded(storedDocuments(entry.documents), added), dropboxLinks: kept.map(({ link, name }) => ({ link, name })) }),
  },
  {
    collection: 'cros',
    section: 'cro',
    label: 'Comptes rendus opératoires',
    links: (entry) => normalizeLinks(entry.dropboxLinks),
    fields: (entry) => ({ patientId: entry.patientId, unit: entry.service, date: toInputDate(entry.datePatient) }),
    update: (entry, added, kept) => ({ documents: withAdded(storedDocuments(entry.documents), added), dropboxLinks: kept.map(({ link }) => link) }),
  },
  {
    collection: 'echos',
    section: 'echo',
    label: 'Échographies',
    links: (entry) => normalizeLinks(entry.fichiers),
    fields: (entry) => ({ patientId: entry.patientId || '', date: toInputDate(examDate(entry)) }),
    update: (entry, added, kept) => ({ documents: withAdded(storedDocuments(entry.documents), added), fichiers: kept.map(({ link, name }) => ({ nom: name, lien: link })) }),
  },
  {
    collection: 'files',
    section: 'programme',
    label: 'Programme opératoire',
    links: (entry) => legacyProgrammeLinks(entry),
    fields: (entry) => ({ unit: entry.service || '', date: toInputDate(entry.startDate) }),
    update: (entry, added, kept) => ({
      documents: withAdded(storedDocuments(entry.documents), added),
      dropboxLinks: kept.map(({ link, name }) => ({ link, name })),
      link: deleteField(),
    }),
  },
  {
    collection: 'gardeAstreinte',
    section: 'gardes',
    label: 'Gardes et astreintes',
    links: (entry) => legacyProgrammeLinks(entry),
    fields: (entry) => ({ unit: entry.service || '', date: toInputDate(entry.startDate || entry.timestamp) }),
    update: (entry, added, kept) => ({
      documents: withAdded(storedDocuments(entry.documents), added),
      dropboxLinks: kept.map(({ link, name }) => ({ link, name })),
      link: deleteField(),
    }),
  },
  {
    collection: 'iconographie',
    section: 'iconographie',
    label: 'Iconographie',
    links: (entry) => (entry.link && !(entry.document && entry.document.id) ? [{ link: entry.link, name: entry.name || 'Image' }] : []),
    fields: (entry) => ({ unit: entry.service || '', date: toInputDate(entry.timestamp) }),
    update: (entry, added) => ({ document: asDocument(added[0]), link: deleteField() }),
  },
  {
    collection: 'filesObs',
    section: 'staff',
    label: 'Staff',
    links: (entry) => (entry.link && !(entry.document && entry.document.id) ? [{ link: entry.link, name: entry.name || 'Document de staff' }] : []),
    fields: (entry) => ({ unit: entry.service, date: toInputDate(entry.timestamp) }),
    update: (entry, added) => ({ document: asDocument(added[0]), link: deleteField() }),
  },
  {
    collection: 'messages',
    section: 'messages',
    label: 'Pièces jointes des messages',
    links: (entry) => (entry.fileURL && !storedDocuments(entry.documents).length ? [{ link: entry.fileURL, name: entry.fileName || 'Pièce jointe' }] : []),
    fields: (entry) => ({ unit: entry.unit || entry.userService || '', date: toInputDate(entry.timestamp) }),
    update: (entry, added) => ({ documents: withAdded(storedDocuments(entry.documents), added), fileURL: deleteField(), fileName: deleteField() }),
  },
  {
    // Protected space: read only once the Archives are opened with the code in this tab.
    collection: 'archives',
    section: 'archives',
    area: 'archives',
    label: 'Archives',
    links: (entry) => normalizeLinks(entry.dropboxLinks),
    fields: (entry) => ({ patientId: entry.patientId || (entry.patient && entry.patient._id) || '', unit: entry.service || '', date: toInputDate(entry.timestamp) }),
    update: (entry, added, kept) => ({ documents: withAdded(storedDocuments(entry.documents), added), dropboxLinks: kept.map(({ link, name }) => ({ link, name })) }),
  },
];

// Administration: copies the files behind the former Dropbox links (personal accounts) into
// the service Dropbox, then updates each entry. Can be stopped and started again at any time.
const DropboxImport = () => {
  const user = useSessionUser();
  const status = useDocumentsStatus();
  const { patients, ensurePatients } = usePatientDoctorContext();
  const [scan, setScan] = useState({ status: 'loading', entries: [], locked: [] }); // entries: [{ source, entry, links }]
  const [run, setRun] = useState({ state: 'idle', done: 0, total: 0, imported: 0, failed: 0, current: '' });
  const [failures, setFailures] = useState([]);
  const [error, setError] = useState('');
  const stop = useRef(false);

  const load = useCallback(async () => {
    setScan((current) => ({ ...current, status: 'loading' }));
    try {
      const open = SOURCES.filter((source) => !source.area || getAreaGrant(source.area));
      const snapshots = await Promise.all(open.map((source) => getDocs(collection(db, source.collection))));
      const entries = snapshots.flatMap((snapshot, index) =>
        snapshot.docs
          .map((item) => ({ source: open[index], entry: { id: item.id, ...item.data() } }))
          .map((row) => ({ ...row, links: row.source.links(row.entry) }))
          .filter((row) => row.links.length > 0)
      );
      setScan({ status: 'ready', entries, locked: SOURCES.filter((source) => !open.includes(source)).map((source) => source.label) });
    } catch (loadError) {
      setScan({ status: 'error', entries: [], locked: [] });
    }
  }, []);

  useEffect(() => {
    ensurePatients().catch(() => {});
    load();
  }, [ensurePatients, load]);

  const running = run.state === 'running' || run.state === 'stopping';
  useEffect(() => {
    if (!running) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [running]);

  const patientsById = useMemo(() => Object.fromEntries(patients.map((patient) => [patient._id, patient])), [patients]);
  const total = scan.entries.reduce((count, row) => count + row.links.length, 0);
  const bySection = SOURCES.map((source) => ({
    label: source.label,
    count: scan.entries.filter((row) => row.source === source).reduce((count, row) => count + row.links.length, 0),
  })).filter((item) => item.count > 0);

  // "Mamadou Ba", "Staff du 12 mars 2026"… to find the entry of a link.
  const contextOf = (source, entry) => {
    if (source.section === 'archives') return { text: `Archive de ${(entry.patient && entry.patient.nom) || 'patient non précisé'} · ${formatDate(entry.timestamp) || 'date inconnue'}`, to: '/archive' };
    if (source.section === 'staff') return { text: `Staff du ${formatDate(entry.timestamp) || 'date inconnue'}${entry.service ? ` · ${entry.service}` : ''}` };
    if (source.section === 'programme') return { text: `${titleOf(entry)} · ${periodLabel(periodOf(entry))}`, to: '/programme' };
    if (source.section === 'iconographie') return { text: `${String(entry.comment || '').trim() || entry.name || 'Image'} · ${formatDate(entry.timestamp) || 'date inconnue'}`, to: '/iconographie' };
    if (source.section === 'messages') return { text: `Message de ${entry.sender || entry.userName || 'auteur inconnu'} · ${formatDate(entry.timestamp) || 'date inconnue'}`, to: '/message' };
    if (source.section === 'gardes') return { text: `${scheduleTitleOf(entry, 'Tableau de garde')} · ${periodLabel(periodOf(entry))}`, to: '/garde' };
    const patient = patientsById[entry.patientId];
    if (patient) return { text: patient.nom, to: `/patients/${patient._id}` };
    if (source.section === 'echo') return { text: patientName(entry) || 'Patient non rattaché' };
    return { text: 'Dossier introuvable' };
  };

  const start = async () => {
    stop.current = false;
    setError('');
    setFailures([]);
    const counts = { done: 0, imported: 0, failed: 0 };
    const failed = [];
    setRun({ state: 'running', total, ...counts, current: '' });

    for (const { source, entry, links } of scan.entries) {
      if (stop.current) break;
      const added = [];
      const kept = [];
      for (const item of links) {
        if (stop.current) {
          kept.push(item);
          continue;
        }
        setRun((current) => ({ ...current, current: `${item.name} · ${contextOf(source, entry).text}` }));
        try {
          // eslint-disable-next-line no-await-in-loop
          const result = await importLink({ link: item.link, name: item.name, section: source.section, ...source.fields(entry) });
          added.push(result.document);
          counts.imported += 1;
        } catch (importError) {
          kept.push(item);
          counts.failed += 1;
          // eslint-disable-next-line no-await-in-loop
          failed.push({ key: `${source.collection}-${entry.id}-${item.link}`, source, entry, item, reason: await documentError(importError, 'Erreur inconnue.') });
          setFailures(failed.slice());
        }
        counts.done += 1;
        setRun((current) => ({ ...current, ...counts }));
      }
      if (added.length) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await updateDoc(doc(db, source.collection, entry.id), {
            ...source.update(entry, added, kept),
            updatedBy: user.name || '',
            updatedAt: new Date().toISOString(),
          });
        } catch (saveError) {
          // The copies are kept by the server: a new run links them without copying again.
          setError('Une entrée n’a pas pu être mise à jour. Relancez la reprise : les fichiers déjà copiés ne le seront pas deux fois.');
        }
      }
    }

    reportImport({ imported: counts.imported, failed: counts.failed });
    setRun((current) => ({ ...current, ...counts, state: stop.current ? 'stopped' : 'done', current: '' }));
    load();
  };

  if (status.ready === false) {
    return (
      <Notice tone="amber" icon={FiAlertCircle}>
        Le Dropbox du service n’est pas encore relié à la plateforme (clés Dropbox à ajouter sur le serveur). La reprise sera
        possible ensuite.
      </Notice>
    );
  }

  const percent = run.total ? Math.round((run.done / run.total) * 100) : 0;

  return (
    <div className="flex flex-col gap-[16px]">
      <section className={`${CARD} p-[20px] sm:p-6`}>
        <p className="m-0 max-w-3xl text-[14.5px] leading-relaxed text-black/60">
          Chaque fichier est copié depuis son ancien lien Dropbox vers le Dropbox du service (fichiers des dossiers, comptes rendus
          opératoires, échographies, staff, programme opératoire, gardes et astreintes, iconographie). L’entrée est ensuite mise à jour : le document s’ouvre dans la plateforme. Les liens
          impossibles à copier (fichier privé ou supprimé) restent en place et sont listés ci-dessous. Vous pouvez arrêter et relancer à
          tout moment : un fichier déjà copié ne l’est pas deux fois.
        </p>

        <div className="mt-[20px] flex flex-col gap-[16px] sm:flex-row sm:items-center sm:justify-between">
          {scan.status === 'loading' && !running ? (
            <div className="w-64">
              <SkeletonRows rows={1} />
            </div>
          ) : scan.status === 'error' ? (
            <Notice tone="red">Les entrées n’ont pas pu être lues. Vérifiez la connexion, puis rechargez la page.</Notice>
          ) : (
            <div className="min-w-0">
              <p className="m-0 text-[15px] text-ink">
                <strong className="text-[22px] font-semibold tabular-nums">{running ? run.total - run.done : total}</strong>{' '}
                ancien{total > 1 ? 's' : ''} lien{total > 1 ? 's' : ''} à reprendre
                <span className="text-black/45"> · {scan.entries.length} entrée{scan.entries.length > 1 ? 's' : ''}</span>
              </p>
              {bySection.length > 0 && (
                <p className="mb-0 mt-1 text-[13px] text-black/45">{bySection.map((item) => `${item.label} : ${item.count}`).join(' · ')}</p>
              )}
              {scan.locked.length > 0 && (
                <p className="mb-0 mt-1 text-[13px] text-black/45">
                  {scan.locked.join(', ')} : ouvrez d’abord l’espace avec votre code dans cet onglet, puis revenez ici pour reprendre ses documents.
                </p>
              )}
            </div>
          )}
          <div className="flex flex-none flex-wrap gap-2">
            {running ? (
              <button
                type="button"
                onClick={() => {
                  stop.current = true;
                  setRun((current) => ({ ...current, state: 'stopping' }));
                }}
                disabled={run.state === 'stopping'}
                className={`${BUTTON.secondary} ${SIZE.md}`}
              >
                <FiPause size={15} aria-hidden="true" />
                {run.state === 'stopping' ? 'Arrêt après ce fichier…' : 'Arrêter'}
              </button>
            ) : (
              <>
                <button type="button" onClick={load} disabled={scan.status === 'loading'} className={`${BUTTON.secondary} ${SIZE.md}`}>
                  <FiRefreshCw size={15} aria-hidden="true" />
                  Actualiser
                </button>
                <button type="button" onClick={start} disabled={scan.status !== 'ready' || !total || status.ready !== true} className={`${BUTTON.primary} ${SIZE.md}`}>
                  <FiPlay size={15} aria-hidden="true" />
                  {run.state === 'idle' ? 'Lancer la reprise' : 'Relancer la reprise'}
                </button>
              </>
            )}
          </div>
        </div>

        {run.state !== 'idle' && (
          <div className="mt-[20px]">
            <div className="h-2 overflow-hidden rounded-full bg-black/[0.06]" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
              <div className="h-full rounded-full bg-brand-600 transition-all" style={{ width: `${percent}%` }} />
            </div>
            <p className="mb-0 mt-2 text-[13.5px] text-black/55" role="status">
              {run.done} / {run.total} · <span className="text-emerald-700">{run.imported} copié{run.imported > 1 ? 's' : ''}</span> ·{' '}
              <span className={run.failed ? 'text-red-600' : ''}>{run.failed} en échec</span>
              {running && run.current && <span className="block truncate text-black/40">En cours : {run.current}</span>}
              {running && <span className="block text-black/40">Gardez cette page ouverte pendant la reprise.</span>}
            </p>
          </div>
        )}
      </section>

      {run.state === 'done' && (
        <Notice tone={run.failed ? 'amber' : 'green'} icon={run.failed ? FiAlertCircle : FiCheckCircle}>
          Reprise terminée : {run.imported} document{run.imported > 1 ? 's' : ''} copié{run.imported > 1 ? 's' : ''} dans le Dropbox du service
          {run.failed ? `, ${run.failed} en échec (liste ci-dessous).` : '.'}
        </Notice>
      )}
      {run.state === 'stopped' && <Notice tone="brand">Reprise arrêtée. Relancez-la pour continuer : rien n’est copié deux fois.</Notice>}
      {error && <Notice tone="red">{error}</Notice>}

      {failures.length > 0 && (
        <section className={`${CARD} overflow-hidden`}>
          <h2 className="m-0 px-[20px] pb-2 pt-[20px] text-[16px] font-semibold tracking-tight text-ink sm:px-6">Liens non repris</h2>
          <p className="m-0 px-[20px] pb-[12px] text-[13px] text-black/45 sm:px-6">
            Ils restent dans leur entrée. Rendez le fichier accessible (ou ajoutez-le à nouveau depuis la plateforme), puis relancez la reprise.
          </p>
          <ul className="m-0 list-none p-0">
            {failures.map(({ key, source, entry, item, reason }) => {
              const context = contextOf(source, entry);
              return (
                <li key={key} className="flex flex-col gap-1 border-0 border-t-[1px] border-solid border-black/[0.05] px-[20px] py-[12px] sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <div className="min-w-0">
                    <p className="m-0 break-all text-[14px] font-medium text-ink">{item.name}</p>
                    <p className="m-0 text-[13px] text-black/50">
                      <span className="text-black/40">{source.label} · </span>
                      {context.to ? (
                        <Link to={context.to} className="text-brand-600 no-underline hover:text-brand-700">
                          {context.text}
                        </Link>
                      ) : (
                        context.text
                      )}
                      {' · '}
                      <span className="text-red-600">{reason}</span>
                    </p>
                  </div>
                  <a href={item.link} target="_blank" rel="noopener noreferrer" className="inline-flex flex-none items-center gap-1 text-[13px] font-medium text-brand-600 no-underline hover:text-brand-700">
                    <FiExternalLink size={12} aria-hidden="true" />
                    Ancien lien
                  </a>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
};

export default DropboxImport;
